import { CornerDownLeft, X } from 'lucide-react';

import { Badge } from '@/components/shadcn/ui/badge';
import {
  InputGroup,
  InputGroupInput,
} from '@/components/shadcn/ui/input-group';
import { cn } from '@/lib/cn';

import { type TagValidationResult,useTagInput } from './use-tag-input';

export interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  // 태그 추가 전 검증 함수 (null 반환 시 통과)
  validateTag?: (tag: string, currentTags: string[]) => TagValidationResult;
  // 태그 추가 시 변환 함수 (null 반환 시 추가하지 않음)
  transformOnAdd?: (tag: string) => string | null;
  // validation 에러 발생 시 콜백
  onValidationError?: (result: TagValidationResult, tag: string) => void;
  /** 입력값 필터 (false 반환 시 입력 차단, 기본: 공백 차단) */
  inputFilter?: (value: string) => boolean;
  disabled?: boolean;
  className?: string;
  showEnterHintBadge?: boolean;
  enterHintText?: string;
}

export function TagInput({
  value,
  onChange,
  placeholder = 'Enter tag',
  validateTag,
  transformOnAdd,
  onValidationError,
  inputFilter,
  disabled = false,
  className,
  showEnterHintBadge = false,
  enterHintText = 'Enter to add',
}: TagInputProps) {
  const { inputValue, handleInputChange, handleKeyDown, removeTag } =
    useTagInput({
      value,
      onChange,
      validateTag,
      transformOnAdd,
      onValidationError,
      inputFilter,
    });

  return (
    <InputGroup
      className={cn(
        'flex-wrap gap-1 p-2 h-auto min-h-9',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
      data-disabled={disabled}
    >
      {value.map((tag, index) => (
        <Badge
          key={`${tag}-${index}`}
          variant="secondary"
          className="flex items-center gap-1"
        >
          <span>{tag}</span>
          {!disabled && (
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="rounded-sm hover:bg-gray-200 transition-colors m-0 flex items-center shrink-0"
              aria-label={`Remove ${tag}`}
            >
              <X className="size-3" />
            </button>
          )}
        </Badge>
      ))}
      <InputGroupInput
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? `${placeholder} ↵` : '↵'}
        disabled={disabled}
        className="flex-1 min-w-20 h-6 px-1 text-sm"
      />
      {showEnterHintBadge && !disabled && inputValue.trim().length > 0 && (
        <Badge variant="secondary" className="pointer-events-none gap-1">
          <CornerDownLeft className="size-3" />
          {enterHintText}
        </Badge>
      )}
    </InputGroup>
  );
}
